/**
 * ConfirmDialog (TMMPDM-0034, ACT-002 / ACT-003 / ACT-007 / DLG-004; TMM-B4, TMMPDM-0065) — the one
 * confirmation. The contract is `{ title, consequence, confirmLabel, cancelLabel?, destructive?,
 * busy?, onConfirm, onCancel }` plus an inline `error`. The title names the object and
 * `consequence` says what happens to it; the confirm button carries the verb and is the one
 * `SubmitButton`. `destructive` makes it `color="error"`; dismiss always comes first and the
 * confirm sits on the right. Dismiss is "Cancel", unless the action is itself a cancel: then the
 * caller passes `cancelLabel` — "Keep subscription" — and `confirmLabel` names the action. Esc and
 * dismiss both cancel; MUI moves focus into the dialog and back to the trigger. `busy` shows the
 * confirm button's in-progress state and blocks a second confirm. `error` shows an action's
 * failure once, inside the open dialog. TMMPDM-0072: `confirmDisabled` says the action can no longer
 * be taken (the object is gone): the confirm is disabled, dismiss takes the focus, and the caller
 * passes `cancelLabel` "Close".
 *
 * Only for consequential, hard-to-undo actions — never for a harmless save (ANTI-018).
 */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from './SubmitButton';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  consequence: ReactNode;
  confirmLabel: string;
  /** The dismiss button's label: "Cancel", or "Keep …" when the action is itself a cancel ("Stay" for unsaved changes). */
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /** The action can no longer be taken: confirm is disabled and the focus moves to dismiss. */
  confirmDisabled?: boolean;
  /** An action error, shown inside the dialog (TMM-B3: once, at the place the person is looking). */
  error?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  consequence,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  confirmDisabled = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useCopy();
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  // The confirm that held the focus is now disabled: keep the focus inside the dialog, on dismiss.
  useEffect(() => {
    if (confirmDisabled) cancelRef.current?.focus();
  }, [confirmDisabled]);
  const cancel = () => {
    if (!busy) onCancel();
  };
  return (
    <Dialog open={open} onClose={cancel} aria-labelledby={titleId} aria-describedby={bodyId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id={bodyId} component="div">
          {consequence}
        </DialogContentText>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button ref={cancelRef} onClick={cancel} disabled={busy}>
          {cancelLabel ?? t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton
          busy={busy}
          disabled={confirmDisabled}
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
        >
          {confirmLabel}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
